import { footprintPolygonsToClipping } from '@alpinehomeair/common';
import { FootprintPolygonsType, PositionType, SpaceType } from 'data/types';
import polygonClipping from 'polygon-clipping';
import {
  clippingOuters,
  geometryFromAbsolutePolygon,
  LeftoverCarvePlan,
  LeftoverGeometry,
  polygonToClipping,
  spaceAbsolutePolygon
} from 'utils/carve-auto-layout-space';
import { MIN_SPACE_DIM_FEET } from 'data/constants';
import { polygonAreaAbs } from 'utils/space-gap-fill';

const MIN_REMNANT_AREA = MIN_SPACE_DIM_FEET * MIN_SPACE_DIM_FEET;

export type RebuildLeftoverResult =
  | { ok: true; plans: LeftoverCarvePlan[] }
  | { ok: false; reason: 'hole' | 'no-footprint' };

export type ResizedSpaceGeometry = {
  uniqueId: string;
  position: PositionType;
  width: number;
  length: number;
  vertices: PositionType[];
  squareFootage: number;
};

export function occupantSpacesForResize(
  userSpaces: SpaceType[],
  resizedSpaceId: string,
  geometry: {
    position: PositionType;
    width: number;
    length: number;
    vertices: PositionType[];
  }
): SpaceType[] {
  return userSpaces.map((space) =>
    space.uniqueId === resizedSpaceId
      ? {
          ...space,
          position: geometry.position,
          width: geometry.width,
          length: geometry.length,
          vertices: geometry.vertices
        }
      : space
  );
}

function leftoverArea(space: SpaceType): number {
  const polygon = spaceAbsolutePolygon(space);
  return polygon ? polygonAreaAbs(polygon) : 0;
}

export function rebuildLeftoverFromOccupants(
  footprintPolygons: FootprintPolygonsType | null | undefined,
  leftovers: SpaceType[],
  occupants: SpaceType[]
): RebuildLeftoverResult {
  if (leftovers.length === 0) {
    return { ok: true, plans: [] };
  }
  const footprintClip = footprintPolygonsToClipping(footprintPolygons ?? []);
  if (footprintClip.length === 0) {
    return { ok: false, reason: 'no-footprint' };
  }

  const occupantPolygons = occupants
    .map((space) => spaceAbsolutePolygon(space))
    .filter((polygon): polygon is PositionType[] => polygon !== null);

  const difference =
    occupantPolygons.length === 0
      ? polygonClipping.union(footprintClip)
      : polygonClipping.difference(
          footprintClip,
          ...occupantPolygons.map((polygon) => polygonToClipping(polygon))
        );

  const { hasHole, polygons } = clippingOuters(difference);
  if (hasHole) {
    return { ok: false, reason: 'hole' };
  }

  const remnants = polygons
    .map((polygon) => ({
      polygon,
      area: polygonAreaAbs(polygon)
    }))
    .filter((entry) => entry.area >= MIN_REMNANT_AREA)
    .sort((a, b) => b.area - a.area);

  const remnantGeometries = remnants
    .map((entry) => geometryFromAbsolutePolygon(entry.polygon))
    .filter((geometry): geometry is LeftoverGeometry => geometry !== null);

  const leftoverOrder = [...leftovers].sort(
    (left, right) => leftoverArea(right) - leftoverArea(left)
  );

  const extraGeometries = remnantGeometries.slice(leftoverOrder.length);
  const plans: LeftoverCarvePlan[] = leftoverOrder.map((leftover, index) => ({
    leftover,
    leftoverGeometry: remnantGeometries[index] ?? null,
    extras: index === 0 ? extraGeometries : []
  }));

  return { ok: true, plans };
}
